import React from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import SubWindow from 'components/SubWindow';
import CustomButton from 'components/button/CustomButton';

import { CsvFieldDefinition } from './types';

type CsvTemplateDownloadDialogProps = {
  /** ダイアログ開閉状態 */
  open: boolean;
  /** ダイアログを閉じるハンドラ */
  onClose: () => void;
  /** エンティティ名（タイトル表示用） */
  entityName: string;
  /** CSVフィールド定義 */
  fields: CsvFieldDefinition[];
  /** ダウンロード実行ハンドラ */
  onDownload: () => void;
};

/**
 * CSVテンプレートダウンロードダイアログ
 * セレクトボックス・ラジオボタン等のコード値対応表を表示し、
 * テンプレートCSVをダウンロードする。
 */
const CsvTemplateDownloadDialog: React.FC<CsvTemplateDownloadDialogProps> = ({
  open,
  onClose,
  entityName,
  fields,
  onDownload,
}) => {
  // マスタ区分・定数のみ対応表に表示（ID型は画面からコピーするため除外）
  const fieldsWithOptions = fields.filter(
    (f) => f.type !== 'id' && f.options && f.options.length > 0,
  );

  return (
    <SubWindow
      title={`${entityName} CSVテンプレート`}
      open={open}
      handleClose={onClose}
      maxWidth="lg"
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            以下のテンプレートCSVをダウンロードし、各列にデータを入力してください。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            IDは各情報の詳細画面タイトル横のコピーボタンから取得できます。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            複数選択の項目はカンマ（,）区切りで入力してください。
          </Typography>
        </Box>

        {fieldsWithOptions.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              入力値対応表
            </Typography>
            {fieldsWithOptions.map((field) => (
              <Box key={field.key} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {field.header}
                  {field.required && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="error.main"
                      sx={{ ml: 0.5 }}
                    >
                      *必須
                    </Typography>
                  )}
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                          入力値（コード）
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>表示名</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {field.options!.map((option) => (
                        <TableRow key={option.code}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {option.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{option.label}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>
        )}

        {/* フィールド一覧 */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            CSVカラム一覧
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ヘッダー名</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>説明</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '60px' }}>必須</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.key}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {field.header}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{field.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={field.required ? 'error.main' : 'text.secondary'}
                      >
                        {field.required ? '○' : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CustomButton
            label="CSVテンプレートをダウンロード"
            buttonVariant="primary"
            icon={<DownloadIcon />}
            onClick={onDownload}
          />
        </Box>
      </Stack>
    </SubWindow>
  );
};

export default CsvTemplateDownloadDialog;
